import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { analyticsApi } from '../services/routinesApi';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend, LineChart, Line, CartesianGrid,
} from 'recharts';
import {
  Activity, TrendingUp, TrendingDown, Loader2, ArrowRight, AlertTriangle, Clock,
  FileSpreadsheet, FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#0EA5E9',
  '#38BDF8', '#22C55E', '#84CC16', '#A855F7', '#EC4899', '#F59E0B', '#64748B'];

const TONE_TEXT = { success: 'text-green-600', warning: 'text-amber-600', danger: 'text-red-600', info: 'text-gray-900' };
const TONE_BG = { success: 'bg-green-50', warning: 'bg-amber-50', danger: 'bg-red-50', info: 'bg-gray-50' };

const fmtVal = (v, format, currency) => {
  if (format === 'currency') {
    const n = Number(v || 0);
    const disp = Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return `${currency || ''} ${disp}`.trim();
  }
  if (format === 'percent') return `${v}%`;
  if (format === 'days') return `${v}d`;
  return Number(v || 0).toLocaleString();
};

const fmtNum = (n) => {
  if (typeof n !== 'number') return n;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
};

const ChartCard = ({ spec }) => {
  if (!spec || !spec.data || spec.data.length === 0) return null;
  const data = spec.data.map((d) => ({ ...d, label: String(d.label) }));
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`analytics-chart-${spec.key}`}>
      <p className="text-sm font-semibold text-gray-700 mb-3">{spec.title}</p>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          {spec.kind === 'donut' ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            </PieChart>
          ) : spec.kind === 'line' ? (
            <LineChart data={data} margin={{ left: 4, right: 16, top: 6, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtNum} width={40} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Line type="monotone" dataKey="value" stroke="#EA580C" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          ) : (
            <BarChart data={data} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtNum} />
              <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 10 }} interval={0} />
              <Tooltip formatter={(v) => fmtNum(v)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const PeriodSelect = ({ value, options, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    data-testid="analytics-period-select"
    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-200">
    {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const ExportControls = ({ module, period }) => {
  const [busy, setBusy] = useState('');
  const download = async (format) => {
    setBusy(format);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/${module}/export?period=${period}&format=${format}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error('export failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `analytics_${module}_${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not generate export');
    } finally { setBusy(''); }
  };
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => download('excel')} disabled={!!busy}
        data-testid="analytics-export-excel"
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
        {busy === 'excel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />} Excel
      </button>
      <button onClick={() => download('pdf')} disabled={!!busy}
        data-testid="analytics-export-pdf"
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
        {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />} PDF
      </button>
    </div>
  );
};

const ModuleDashboard = ({ view }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  useEffect(() => {
    setLoading(true);
    const fn = view === 'p2p' ? analyticsApi.p2p : analyticsApi.otc;
    fn(period).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [view, period]);

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading analytics…</div>;
  if (!data) return <div className="text-center text-gray-400 py-20">No analytics available.</div>;

  const Icon = view === 'p2p' ? TrendingDown : TrendingUp;
  return (
    <div data-testid={`analytics-${view}`}>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-600 text-white"><Icon className="h-5 w-5" /></div>
          <h1 className="text-2xl font-bold text-gray-900">{data.title}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodSelect value={period} options={data.period_options} onChange={setPeriod} />
          <ExportControls module={view} period={period} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {data.kpis.map((k) => (
          <div key={k.key} className={`rounded-2xl border border-gray-200 p-4 ${TONE_BG[k.tone] || 'bg-white'}`} data-testid={`analytics-kpi-${k.key}`}>
            <p className="text-xs font-medium text-gray-500">{k.label}</p>
            <p className={`text-2xl font-bold mt-1 ${TONE_TEXT[k.tone] || 'text-gray-900'}`}>{fmtVal(k.value, k.format, data.currency)}</p>
            {k.hint && <p className="text-[11px] text-gray-400 mt-0.5">{k.hint}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {data.charts.map((c) => <ChartCard key={c.key} spec={c} />)}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="analytics-process">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-700">Process Analytics — Stage Aging & Bottlenecks</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-4 font-medium">Stage</th>
                <th className="py-2 px-4 font-medium text-right">Open Docs</th>
                <th className="py-2 px-4 font-medium text-right">Avg Age</th>
                <th className="py-2 px-4 font-medium text-right">Oldest</th>
                <th className="py-2 pl-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.process.map((p) => (
                <tr key={p.stage} className="border-b border-gray-50" data-testid={`analytics-stage-${p.stage.replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="py-2.5 pr-4 font-medium text-gray-800">{p.stage}</td>
                  <td className="py-2.5 px-4 text-right text-gray-700">{p.open_count}</td>
                  <td className="py-2.5 px-4 text-right text-gray-700">{p.avg_age_days}d</td>
                  <td className="py-2.5 px-4 text-right text-gray-700">{p.oldest_days}d</td>
                  <td className="py-2.5 pl-4">
                    {p.is_bottleneck ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> Bottleneck</span>
                    ) : p.open_count === 0 ? (
                      <span className="text-xs text-green-600">Clear</span>
                    ) : (
                      <span className="text-xs text-gray-400">Flowing</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Overview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  useEffect(() => {
    setLoading(true);
    analyticsApi.overview(period).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period]);

  if (loading && !data) return <div className="flex items-center gap-2 text-gray-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="analytics-overview">
      <div className="flex items-center justify-between mb-1 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-600 text-white"><Activity className="h-5 w-5" /></div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Overview</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodSelect value={period} options={data?.period_options} onChange={setPeriod} />
          <ExportControls module="overview" period={period} />
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-8">Cross-cycle financial and operational KPIs across Procure-to-Pay and Order-to-Cash.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {(data?.cards || []).map((c, i) => (
          <button key={i} onClick={() => navigate(c.link)} data-testid={`analytics-overview-card-${i}`}
            className={`text-left rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all ${TONE_BG[c.tone] || 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{c.module}</span>
              <ArrowRight className="h-4 w-4 text-gray-300" />
            </div>
            <p className="text-sm text-gray-600 mt-2">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${TONE_TEXT[c.tone] || 'text-gray-900'}`}>{fmtVal(c.value, c.format, data?.currency)}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => navigate('/analytics/p2p')} data-testid="analytics-goto-p2p"
          className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-5 hover:border-sky-300 hover:shadow-md transition-all">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><TrendingDown className="h-5 w-5" /></div>
          <div className="flex-1 text-left"><p className="font-semibold text-gray-900">Procure-to-Pay Analytics</p><p className="text-xs text-gray-500">Spend, vendors, PO cycle, AP aging</p></div>
          <ArrowRight className="h-5 w-5 text-gray-300" />
        </button>
        <button onClick={() => navigate('/analytics/otc')} data-testid="analytics-goto-otc"
          className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-5 hover:border-sky-300 hover:shadow-md transition-all">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600"><TrendingUp className="h-5 w-5" /></div>
          <div className="flex-1 text-left"><p className="font-semibold text-gray-900">Order-to-Cash Analytics</p><p className="text-xs text-gray-500">Revenue, backlog, fulfilment, AR aging</p></div>
          <ArrowRight className="h-5 w-5 text-gray-300" />
        </button>
      </div>
    </div>
  );
};

const Analytics = ({ view = 'overview' }) => (
  <Layout>
    <div className="p-6 max-w-6xl mx-auto">
      {view === 'overview' ? <Overview /> : <ModuleDashboard view={view} />}
    </div>
  </Layout>
);

export default Analytics;
